import {ButtonV2 as Button} from '@jobseeker/lotus/elements';
import {
  Box,
  Typography as Typo,
  Card as MuiCard,
  styled,
  TypographyProps,
  Skeleton,
} from '@mui/material';
import {NumberComponent} from './NumberComponent';
import {PasswordComponent} from './PasswordComponent';
import {VerificationComponent} from './VerificationComponent';
import {TwoStepAuthenticationDialogComponent} from './TwoStepComponent';
import {useState} from 'react';
import CustomDialog from '@jobseeker/lotus/components/DialogWithBottomSheet';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const SecuritySettingWidget = (props: {loading: boolean}) => {
  const {profile} = useSelector((state: AppState) => state.user);
  const {loading} = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogSubTitle, setDialogSubTitle] = useState('');
  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );
  const isLarge = useIsLargeDevice();
  const openDialog =
    (title: string, content: React.ReactNode, subTitle?: string) => () => {
      setDialogTitle(title);
      if (subTitle) setDialogSubTitle(subTitle);
      setDialogContent(content);
      setDialogOpen(true);
    };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 4,
          marginLeft: {md: 2},
        }}
      >
        <Box>
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={{xxs: 600, md: 500}}
              m={2}
              mt={3}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Security & Login
            </Typography>
          )}
          {loading ? (
            <Box px={1} pb={4}>
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
              {[
                ...new Array(5).fill('').map(() => (
                  <>
                    <Skeleton
                      variant="rounded"
                      width={'100%'}
                      height={14}
                      sx={{
                        my: 0.5,
                      }}
                    />
                  </>
                )),
              ]}
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
            </Box>
          ) : (
            <Box mt={isLarge ? 0 : 3}>
              <Box
                color="gray.dark"
                onClick={() => {}}
                data-testid="notification_list_item"
                sx={{
                  position: 'relative',
                  transition: 'all ease 0.2s',
                  cursor: 'pointer',
                  // borderBottom: 'solid 1px',
                  // borderColor: 'border.lightGray',
                }}
              >
                <Box
                  sx={{
                    pt: {xxs: 1, md: 2},
                    pb: {xxs: 1, md: 6.5},
                    px: {xxs: 1, md: 2},
                  }}
                >
                  <Box>
                    <Box
                      display={'flex'}
                      mt={-1}
                      sx={{
                        width: {xxs: '100%', md: 680},
                      }}
                    >
                      <Box display={'flex'} flexDirection={'column'}>
                        <Typography
                          color={'#333333'}
                          fontWeight={500}
                          fontSize={14}
                          sx={{
                            lineClamp: '3',
                            '-webkit-line-clamp': '2',
                            '-webkit-box-orient': 'vertical',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            wordWrap: 'break-word',
                            display: 'block',
                            alignItems: 'center',
                          }}
                        >
                          2-Step Verification
                        </Typography>
                        <Typography
                          fontWeight={400}
                          fontSize={14}
                          sx={{
                            color: '#7E7E7E',
                            lineClamp: '3',
                            '-webkit-line-clamp': '2',
                            '-webkit-box-orient': 'vertical',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            wordWrap: 'break-word',
                            display: 'block',
                            alignItems: 'center',
                          }}
                        >
                          Secure your account with 2-Step verification, which
                          adds extra security by requiring a verification code
                          whenever you login
                        </Typography>
                        {!!!userSettings.twoFactorAuth && (
                          <Button
                            variant="text"
                            onClick={
                              profile?.phone
                                ? openDialog(
                                    'Enable 2-Step Verification',
                                    <TwoStepAuthenticationDialogComponent
                                      setDialogOpen={setDialogOpen}
                                      setTitle={setDialogTitle}
                                      setSubTitle={setDialogSubTitle}
                                      setDialogContent={setDialogContent}
                                    />,
                                    "Each time you login to your iimjobs.com account, you'll need your password and a verification code sent on this number"
                                  )
                                : openDialog(
                                    'Enable 2-Step Verification',
                                    <NumberComponent
                                      setDialogOpen={setDialogOpen}
                                      setTitle={setDialogTitle}
                                      setSubTitle={setDialogSubTitle}
                                      setDialogContent={setDialogContent}
                                      isForChangeNumber={false}
                                    />,
                                    "Each time you login to your iimjobs.com account, you'll need your password and a verification code sent on this number"
                                  )
                            }
                            sx={{
                              padding: '0px',
                              fontSize: '14px',
                              fontWeight: '500',
                              mt: '2px',
                              mb: {
                                xxs: 1.5,
                                md: !!userSettings.twoFactorAuth ? 1 : 16.5,
                              },
                              color: 'var(--brand-color, #49966E)',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              '&:hover': {
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            Enable 2-Step Verification
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <CustomDialog
                      open={dialogOpen}
                      title={dialogTitle}
                      subTitle={dialogSubTitle}
                      Content={dialogContent}
                      onClose={closeDialog}
                    />
                    {!!userSettings.twoFactorAuth && (
                      <VerificationComponent
                        toggleNumberDialog={openDialog(
                          'Change Mobile Number',
                          <NumberComponent
                            setDialogOpen={setDialogOpen}
                            setTitle={setDialogTitle}
                            setSubTitle={setDialogSubTitle}
                            setDialogContent={setDialogContent}
                            isForChangeNumber={true}
                          />,
                          'This number will be used for 2-step verification, updated on your profile, and shown to recruiters when you apply for jobs'
                        )}
                        toggleDisableDialog={openDialog(
                          'Please enter your password to disable 2-Step Verification',
                          <PasswordComponent
                            setDialogOpen={setDialogOpen}
                            setTitle={setDialogTitle}
                            setSubTitle={setDialogSubTitle}
                            setDialogContent={setDialogContent}
                          />,
                          'You will no longer receive a verification code when you login to your iimjobs.com account'
                        )}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};

export default SecuritySettingWidget;
